import PropTypes from "prop-types";

import { StreetField } from "UI/FnrAddress";

import { useAutocompleteContext } from "./AutocompleteContext";
import ShadowField from "./ShadowField";
import UspsVerifiedBadge from "../badge/UspsVerifiedBadge";

/**
 * Street field auto-wired to the autocomplete context. Reads value,
 * onChange, shadows.addr1, addr1Ref, onFieldBlur, multiUnitFill (the inline
 * unit-fill descriptor), and `uspsVerified` from the context provided by
 * `AddressAutocompleteRoot value={...}`. Consumer-controlled props (label,
 * disabled) still pass through explicitly.
 *
 * The USPS-Verified badge sits inline beside the field's label on every
 * surface. It is always mounted and its own `FnrOpacityToggle` fades it in/out
 * on `uspsVerified` (driven through `show`) — so it shows only for a Smarty
 * verdict (an admin override is verified but not USPS) and the transition
 * fades instead of popping. A surface that already displays its verified state
 * elsewhere (the admin profile's read-only `VerificationBadge`) passes
 * `hideVerifiedBadge` so its editing view doesn't double it up.
 */
export default function AutocompleteStreetField({
  label,
  disabled,
  inputStyle,
  acceptRightOffset,
  hideVerifiedBadge,
}) {
  const {
    value,
    onChange,
    shadows,
    onFieldBlur,
    addr1Ref,
    multiUnitFill,
    uspsVerified,
  } = useAutocompleteContext();
  const typed = value.addr1 || "";
  return (
    <ShadowField
      inputRef={addr1Ref}
      typed={typed}
      suffix={shadows.addr1}
      unitFill={multiUnitFill}
      acceptRightOffset={acceptRightOffset}
    >
      <StreetField
        ref={addr1Ref}
        disabled={disabled}
        label={label}
        labelBadge={
          hideVerifiedBadge ? undefined : (
            <UspsVerifiedBadge show={uspsVerified} />
          )
        }
        value={typed}
        onChange={(e) => onChange({ addr1: e.target.value })}
        onBlur={() => onFieldBlur("addr1")}
        placeholder={shadows.addr1 ? "" : undefined}
        style={inputStyle}
      />
    </ShadowField>
  );
}

AutocompleteStreetField.propTypes = {
  label: PropTypes.string,
  disabled: PropTypes.bool,
  // Inline style for the street input — surfaces that nest the country flag
  // inside the input's right edge use it to reserve that corner (right
  // padding).
  inputStyle: PropTypes.object,
  // Inset for the inline unit-fill affordance, forwarded to ShadowField — bump it when
  // the nested country flag occupies the input's right edge.
  acceptRightOffset: PropTypes.number,
  // Suppress the inline USPS-Verified badge for a surface that surfaces its
  // verified state elsewhere (the admin profile's read-only view).
  hideVerifiedBadge: PropTypes.bool,
};
